import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { CircularProgressIndicator, ListItem, Surface, useSurfaceColor } from '../..';
import FloatingPortal from '../../atoms/FloatingPortal';
import { usePrevious } from '../../utils';
import { useTheme } from '@emotion/react';
import { autoPlacement, autoUpdate, offset, shift, size, useClick, useDismiss, useFloating, useFloatingNodeId, useInteractions, useRole, } from '@floating-ui/react';
import { motion } from 'framer-motion';
import { createContext, useContext, useEffect, useState, useTransition, } from 'react';
export const MENU_OFFSET = 12;
const MenuContext = createContext({
    nodeId: '',
    x: 0,
    y: 0,
    strategy: 'absolute',
    placement: 'bottom-start',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refs: {},
    getReferenceProps: props => props,
    getFloatingProps: props => props,
    getItemProps: props => props,
    open: false,
    setOpen: () => { },
});
const MenuButton = ({ children, ...props }) => {
    const { refs, getReferenceProps, open } = useContext(MenuContext);
    return (_jsx("div", { ref: refs.setReference, ...getReferenceProps(props), css: { width: 'fit-content', cursor: 'pointer' }, children: typeof children === 'function' ? children({ open }) : children }));
};
const MenuItems = (props) => {
    const theme = useTheme();
    const [animating, setAnimating] = useState(false);
    const { nodeId, x, y, strategy, refs, getFloatingProps, open, setOpen } = useContext(MenuContext);
    const children = typeof props.children === 'function'
        ? props.children({ open, toggleOpen: () => setOpen(open => !open) })
        : props.children;
    return (_jsx(FloatingPortal, { id: nodeId, children: (open || animating) && (_jsx(Surface, { as: motion.section, ref: refs.setFloating, elevation: x => x + 1, onAnimationStart: () => setAnimating(true), onAnimationComplete: () => setAnimating(false), variants: {
                true: {
                    scale: 1,
                    opacity: 1,
                    transition: {
                        delayChildren: 0.15,
                        staggerChildren: 0.025,
                    },
                    transitionEnd: {
                        overflow: 'auto',
                    },
                },
                false: {
                    scale: 0.95,
                    opacity: 0,
                    overflow: 'hidden',
                },
            }, css: {
                border: `1px solid ${theme.color.outlineVariant}`,
                borderRadius: theme.shape.small,
                overflow: 'auto',
                '.talismn-ui-menu-item:first-child': { marginTop: '0.8rem' },
                '.talismn-ui-menu-item:last-child': { marginBottom: '0.8rem' },
            }, ...getFloatingProps({
                ...props,
                style: { ...props.style, position: strategy, top: y ?? 0, left: x ?? 0, width: 'max-content' },
                children,
            }) })) }));
};
const renderMenuItemChildren = (children, renderProps) => typeof children === 'function' ? children(renderProps) : children;
const MenuItem = Object.assign(({ children: childrenOrRenderFunc, dismissAfterSelection = true, withTransition = false, onClick, disabled, ...props }) => {
    const theme = useTheme();
    const { getItemProps, setOpen } = useContext(MenuContext);
    const [isPending, startTransition] = useTransition();
    const prevIsPending = usePrevious(isPending);
    useEffect(() => {
        if (!isPending && prevIsPending) {
            setOpen(false);
        }
    }, [isPending, prevIsPending, setOpen]);
    const [hover, setHover] = useState(false);
    const children = renderMenuItemChildren(childrenOrRenderFunc, { hover, isPending });
    return (_jsx("button", { onClick: () => {
            const maybeStartTransition = withTransition ? startTransition : (callback) => callback();
            maybeStartTransition(() => {
                onClick?.();
                if (dismissAfterSelection && !withTransition) {
                    setOpen(false);
                }
            });
        }, disabled: disabled, css: {
            display: 'contents',
            textAlign: 'unset',
            cursor: 'pointer',
            ':disabled': {
                cursor: 'not-allowed',
                '> *': {
                    filter: 'brightness(0.5)',
                },
            },
            ':not(:disabled)': {
                '> *:hover': {
                    backgroundColor: useSurfaceColor(),
                },
            },
        }, children: _jsx(motion.div, { className: ['talismn-ui-menu-item', props.className].join(' '), variants: {
                true: { opacity: 1, transform: 'translateY(0px)' },
                false: { opacity: 0, transform: 'translateY(20px)' },
            }, css: {
                margin: '0 0.8rem',
                borderRadius: theme.shape.extraSmall,
            }, onHoverStart: () => setHover(true), onHoverEnd: () => setHover(false), ...getItemProps({
                ...props,
                children,
            }) }) }));
}, {
    Button: ({ headlineContent, overlineContent, supportingContent, leadingContent, trailingContent, revealTrailingContentOnHover, ...props }) => (_jsx(MenuItem, { ...props, children: renderProps => (_jsx(ListItem, { headlineContent: renderMenuItemChildren(headlineContent, renderProps), overlineContent: renderMenuItemChildren(overlineContent, renderProps), supportingContent: renderMenuItemChildren(supportingContent, renderProps), leadingContent: renderMenuItemChildren(leadingContent, renderProps), trailingContent: renderProps.isPending ? (_jsx(CircularProgressIndicator, { size: "1em" })) : (renderMenuItemChildren(trailingContent, renderProps)), revealTrailingContentOnHover: revealTrailingContentOnHover })) })),
});
const Menu = (props) => {
    const nodeId = useFloatingNodeId();
    const [open, setOpen] = useState(false);
    const { context, x, y, refs, strategy, placement } = useFloating({
        nodeId,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(MENU_OFFSET),
            autoPlacement({ allowedPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'] }),
            shift(),
            size({
                apply: ({ availableHeight, elements }) => {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${availableHeight}px`,
                    });
                },
            }),
        ],
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useRole(context, { role: 'menu' }),
        useClick(context),
        useDismiss(context),
    ]);
    return (_jsx(MenuContext.Provider, { value: {
            nodeId,
            x,
            y,
            strategy,
            placement,
            refs,
            getReferenceProps,
            getFloatingProps,
            getItemProps,
            open,
            setOpen,
        }, children: _jsx(motion.div, { initial: String(false), animate: String(open), children: props.children }) }));
};
export default Object.assign(Menu, { Button: MenuButton, Items: MenuItems, Item: MenuItem });
//# sourceMappingURL=Menu.js.map